import { format } from 'date-fns';
import type { PrioridadTarea, Tarea } from '@/types/tareas';

export const ORDEN_PASO = 1000;

export const PRIORIDAD_ESTILO: Record<PrioridadTarea, { label: string; badge: string; dot: string }> = {
  baja:    { label: 'Baja',    badge: 'bg-slate-100 text-slate-600 border-slate-200',  dot: 'bg-slate-400' },
  media:   { label: 'Media',   badge: 'bg-blue-50 text-blue-700 border-blue-200',      dot: 'bg-blue-500' },
  alta:    { label: 'Alta',    badge: 'bg-amber-50 text-amber-700 border-amber-200',   dot: 'bg-amber-500' },
  urgente: { label: 'Urgente', badge: 'bg-red-50 text-red-700 border-red-200',         dot: 'bg-red-500' },
};

export type EstadoVencimiento = 'completada' | 'vencida' | 'hoy' | 'proxima' | 'sin_fecha';

export function estadoVencimiento(tarea: Pick<Tarea, 'fechaVencimiento' | 'completadaAt'>): EstadoVencimiento {
  if (tarea.completadaAt) return 'completada';
  if (!tarea.fechaVencimiento) return 'sin_fecha';
  const fecha = new Date(tarea.fechaVencimiento);
  const ahora = new Date();
  if (fecha < ahora) return 'vencida';
  const finDeHoy = new Date(ahora);
  finDeHoy.setHours(23, 59, 59, 999);
  return fecha <= finDeHoy ? 'hoy' : 'proxima';
}

export const VENCIMIENTO_ESTILO: Record<EstadoVencimiento, string> = {
  completada: 'text-emerald-600',
  vencida: 'text-red-600 font-semibold',
  hoy: 'text-amber-600 font-semibold',
  proxima: 'text-text-secondary',
  sin_fecha: 'text-text-secondary',
};

export function formatearVencimiento(iso: string | null): string {
  if (!iso) return 'Sin fecha';
  const d = new Date(iso);
  const hayHora = d.getHours() !== 0 || d.getMinutes() !== 0;
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
    + (hayHora ? ` · ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}` : '');
}

/** ISO (UTC) → valor para <input type="datetime-local"> en hora local. */
export function isoAInputLocal(iso: string | null | undefined): string {
  return iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : '';
}

/** Valor de <input type="datetime-local"> (hora local) → ISO UTC. */
export function inputLocalAIso(valor: string): string | null {
  return valor ? new Date(valor).toISOString() : null;
}

/** Mismo cálculo que el backend (TareasService.mover) para que la UI optimista coincida con el servidor. */
export function calcularOrden(
  columna: Pick<Tarea, 'id' | 'orden'>[],
  indice: number,
): number {
  const previa = columna[indice - 1];
  const siguiente = columna[indice];
  if (previa && siguiente) return (previa.orden + siguiente.orden) / 2;
  if (previa) return previa.orden + ORDEN_PASO;
  if (siguiente) return siguiente.orden - ORDEN_PASO;
  return ORDEN_PASO;
}

export function formatearFechaHora(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

export function formatearBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function rangoDeHoy() {
  const hoyInicio = new Date();
  hoyInicio.setHours(0, 0, 0, 0);
  const hoyFin = new Date(hoyInicio);
  hoyFin.setHours(23, 59, 59, 999);
  const semanaFin = new Date(hoyInicio);
  semanaFin.setDate(semanaFin.getDate() + 7);
  semanaFin.setHours(23, 59, 59, 999);
  return { hoyInicio, hoyFin, semanaFin };
}
